import { useState, useCallback } from 'react';
import { HexColorPicker } from 'react-colorful';

const PRESET_COLORS = [
  '#e53e3e',
  '#ed8936',
  '#d69e2e',
  '#48bb78',
  '#38a169',
  '#4299e1',
  '#3182ce',
  '#667eea',
  '#805ad5',
  '#d53f8c',
  '#718096',
  '#2d3748',
];

function hexToHsl(hex: string): {
  h: number;
  s: number;
  l: number;
} {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let h = 0;
  let s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        h = ((b - r) / d + 2) / 6;
        break;
      case b:
        h = ((r - g) / d + 4) / 6;
        break;
    }
  }
  return {
    h: Math.round(h * 360),
    s: Math.round(s * 100),
    l: Math.round(l * 100),
  };
}

function hslToHex(h: number, s: number, l: number): string {
  const hNorm = h / 360;
  const sNorm = s / 100;
  const lNorm = l / 100;
  let r: number, g: number, b: number;
  if (sNorm === 0) {
    r = g = b = lNorm;
  } else {
    const hue2rgb = (p: number, q: number, t: number) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    };
    const q =
      lNorm < 0.5
        ? lNorm * (1 + sNorm)
        : lNorm + sNorm - lNorm * sNorm;
    const p = 2 * lNorm - q;
    r = hue2rgb(p, q, hNorm + 1 / 3);
    g = hue2rgb(p, q, hNorm);
    b = hue2rgb(p, q, hNorm - 1 / 3);
  }
  const toHex = (x: number) =>
    Math.round(x * 255)
      .toString(16)
      .padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

function isValidHex(hex: string): boolean {
  return /^#[0-9a-fA-F]{6}$/.test(hex);
}

interface Props {
  value: string;
  onChange: (hex: string) => void;
  disabled?: boolean;
}

export default function ColorPicker({
  value,
  onChange,
  disabled = false,
}: Props) {
  const [isAdvancedOpen, setIsAdvancedOpen] =
    useState(false);

  const [hexInput, setHexInput] = useState(value);
  const [prevValue, setPrevValue] = useState(value);

  if (value !== prevValue) {
    setPrevValue(value);
    setHexInput(value);
  }

  const hsl = isValidHex(value)
    ? hexToHsl(value)
    : { h: 0, s: 0, l: 0 };

  const handleWheelChange = useCallback(
    (hex: string) => {
      if (!disabled) {
        onChange(hex);
      }
    },
    [disabled, onChange]
  );

  const handleHexInputChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const raw = e.target.value;
    setHexInput(raw);
    const normalized = raw.startsWith('#')
      ? raw
      : `#${raw}`;
    if (isValidHex(normalized)) {
      onChange(normalized);
    }
  };

  const handleHexInputBlur = () => {
    const normalized = hexInput.startsWith('#')
      ? hexInput
      : `#${hexInput}`;
    if (isValidHex(normalized)) {
      setHexInput(normalized);
    } else {
      setHexInput(value);
    }
  };

  const handleHChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const h = Math.max(
      0,
      Math.min(360, Number(e.target.value))
    );
    if (!isNaN(h)) {
      onChange(hslToHex(h, hsl.s, hsl.l));
    }
  };

  const handleSChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const s = Math.max(
      0,
      Math.min(100, Number(e.target.value))
    );
    if (!isNaN(s)) {
      onChange(hslToHex(hsl.h, s, hsl.l));
    }
  };

  const handleLChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const l = Math.max(
      0,
      Math.min(100, Number(e.target.value))
    );
    if (!isNaN(l)) {
      onChange(hslToHex(hsl.h, hsl.s, l));
    }
  };

  return (
    <div
      className={[
        'space-y-3',
        disabled ? 'opacity-50 pointer-events-none' : '',
      ].join(' ')}
    >
      {/* Presets row + Advanced toggle */}
      <div className="flex items-center gap-2 flex-wrap">
        {PRESET_COLORS.map(c => (
          <button
            key={c}
            type="button"
            onClick={() => onChange(c)}
            className="w-6 h-6 rounded-full border border-paper-200 shadow-sm hover:scale-110 transition-transform shrink-0"
            style={{ backgroundColor: c }}
            aria-label={`Preset color ${c}`}
          />
        ))}
        <button
          type="button"
          onClick={() => setIsAdvancedOpen(o => !o)}
          className="ml-auto flex items-center gap-1 font-mono text-xs text-ink-400 hover:text-ink-700 transition-colors shrink-0"
        >
          Advanced
          <svg
            className={[
              'w-3 h-3 transition-transform',
              isAdvancedOpen ? 'rotate-180' : '',
            ].join(' ')}
            viewBox="0 0 12 12"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
          >
            <path d="M2 4l4 4 4-4" />
          </svg>
        </button>
      </div>

      {/* Color wheel — always visible */}
      <div className="h-48 [&_.react-colorful]:w-full [&_.react-colorful]:h-full [&_.react-colorful]:rounded-sm [&_.react-colorful__saturation]:rounded-t-sm [&_.react-colorful__last-control]:rounded-b-sm">
        <HexColorPicker
          color={isValidHex(value) ? value : '#3182ce'}
          onChange={handleWheelChange}
          style={{ width: '100%', height: '100%' }}
        />
      </div>

      {/* Advanced panel — hex + HSL inputs */}
      {isAdvancedOpen && (
        <div className="flex items-center gap-2">
          <div className="flex flex-col gap-0.5 flex-1">
            <label className="font-mono text-[10px] text-ink-400 uppercase tracking-widest">
              Hex
            </label>
            <input
              type="text"
              value={hexInput}
              onChange={handleHexInputChange}
              onBlur={handleHexInputBlur}
              maxLength={7}
              className="input-field text-xs font-mono py-1"
              placeholder="#000000"
              spellCheck={false}
            />
          </div>
          <div className="flex flex-col gap-0.5 w-14">
            <label className="font-mono text-[10px] text-ink-400 uppercase tracking-widest">
              H
            </label>
            <input
              type="number"
              min={0}
              max={360}
              value={hsl.h}
              onChange={handleHChange}
              className="input-field text-xs font-mono py-1"
            />
          </div>
          <div className="flex flex-col gap-0.5 w-14">
            <label className="font-mono text-[10px] text-ink-400 uppercase tracking-widest">
              S
            </label>
            <input
              type="number"
              min={0}
              max={100}
              value={hsl.s}
              onChange={handleSChange}
              className="input-field text-xs font-mono py-1"
            />
          </div>
          <div className="flex flex-col gap-0.5 w-14">
            <label className="font-mono text-[10px] text-ink-400 uppercase tracking-widest">
              L
            </label>
            <input
              type="number"
              min={0}
              max={100}
              value={hsl.l}
              onChange={handleLChange}
              className="input-field text-xs font-mono py-1"
            />
          </div>
        </div>
      )}
    </div>
  );
}
